import { ConfirmModal, RenameModal } from '@vrplatform/components';
import {
  type ApiDeleteError,
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
  useMutation,
} from '@vrplatform/data';
import { showErrorNotification } from '@vrplatform/theme';
import {
  showSuccessNotification,
  showWarnNotification,
} from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import type { ConnectionRow } from './useConnectionTableQuery';
import { useNavigate } from 'react-router';
import { useConnectionDetailDrawer } from '../../../../drawers/connection-drawers/useConnectionDetailDrawer';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  connection: Maybe<ConnectionRow>;
}

export const ConnectionEllipsisMenuModals = ({
  data,
  deleteModal,
  renameModal,
  updateModal,
}: {
  data: Maybe<ConnectionRow>;
  deleteModal: {
    opened: boolean;
    close: () => void;
    onSuccess?: () => void;
  };
  updateModal: {
    opened: boolean;
    close: () => void;
  };
  renameModal: {
    opened: boolean;
    close: () => void;
  };
}) => {
  const id = data?.id;

  const invalidateQueries = useInvalidateApiQueries();
  const invalidate = () => {
    invalidateQueries(['connections']);
    invalidateQueries(['connections']);
    invalidateQueries(['connections']);
  };

  const connectionStatus = data?.status || 'active';
  const connectionName = data?.name;
  const isArchived = connectionStatus !== 'active';

  const {
    mutate: updateConnectionStatus,
    loading: loadingUpdateConnectionStatus,
  } = useMutation(
    (
      q,
      args: { id: string; status: 'active' | 'archived'; tenantId: string }
    ) => {
      const connection = q.updateConnectionById({
        pk_columns: { id: args.id },
        _set: {
          status: args.status === 'archived' ? 'inactive' : 'active',
        },
      });

      if (args.status === 'archived') {
        q.updateAutomations({
          where: {
            tenantId: { _eq: args.tenantId },
            _or: [
              {
                leftConnectionId: { _eq: args.id },
              },
              {
                rightConnectionId: { _eq: args.id },
              },
            ],
          },
          _set: {
            status: 'inactive',
          },
        })?.affected_rows || 0;
      }

      return {
        ok: !!connection?.id,
      };
    },
    {
      successMessage: {
        id: `update-connection-${data?.id || ''}`,
        title: 'Success!',
        message: 'Successfully updated connection.',
      },
      invalidateQueryKeys: ['connections', 'scheduledEvents'],
    }
  );

  const handleConnectionArchive = async () => {
    if (!id) return { isSuccess: false, message: 'Connection ID is missing.' };
    if (!data?.tenantId)
      return { isSuccess: false, message: 'Tenant ID is missing.' };

    try {
      const res = await updateConnectionStatus({
        args: {
          id,
          status: isArchived ? 'active' : 'archived',
          tenantId: data?.tenantId,
        },
      });
      if (!res?.ok) throw new Error('Connection update failed.');
      invalidate();
      updateModal.close();
    } catch (error: any) {
      console.error(error);
      return {
        isSuccess: false,
        message: error?.message || 'Failed to update connection.',
      };
    }
  };

  const { mutate: updateConnectionTitle } = useMutation(
    (q, args: { id: string; title: string }) => {
      const connection = q.updateConnectionById({
        pk_columns: { id: args.id },
        _set: {
          name: args.title,
        },
      });

      return {
        ok: !!connection?.id,
      };
    },
    {
      successMessage: {
        id: `update-connection-${data?.id || ''}`,
        title: 'Success!',
        message: 'Successfully updated connection.',
      },
      invalidateQueryKeys: ['connections', 'scheduledEvents'],
    }
  );

  return (
    <>
      <Delete
        opened={deleteModal.opened}
        closeModal={deleteModal.close}
        connection={data}
      />
      <ConfirmModal
        type="confirm"
        loading={loadingUpdateConnectionStatus}
        opened={updateModal.opened}
        closeModal={updateModal.close}
        onSubmit={handleConnectionArchive}
        title={isArchived ? 'Enable connection' : 'Archive connection'}
        subtitle={
          isArchived
            ? `Would you like to re-enable ${connectionName || 'this connection'}? The system will perform scheduled fetches on this connection again.`
            : `Are you sure you want to archive ${
                connectionName || 'this connection'
              }? This will disable all automations connected to this connection.`
        }
        submitTitle={isArchived ? 'Enable' : 'Archive'}
        icon={isArchived ? 'RefreshCwIcon' : 'ArchiveIcon'}
      />
      <RenameModal
        opened={renameModal.opened}
        onClose={renameModal.close}
        title="Edit Connection Name"
        inputTitle="Name"
        inputPlaceholder="Enter your connection name"
        defaultValue={connectionName}
        onSubmit={async (value) => {
          if (!id)
            return showErrorNotification({
              color: 'yellow',
              message: 'Connection ID is missing.',
              title: 'Missing input',
            });

          try {
            await updateConnectionTitle({
              args: {
                id,
                title: value,
              },
            });
            invalidate();
            renameModal.close();
          } catch (error) {
            console.error(error);
          }
        }}
      />
    </>
  );
};

const Delete = ({ opened, closeModal, connection }: ModalProps) => {
  const connectionId = connection?.id;
  const invalidate = useInvalidateApiQueries(['connections']);

  const { opened: detailDrawerOpened, close: closeConnectionDetailDrawer } =
    useConnectionDetailDrawer();

  const goto = useNavigate();

  const { mutateAsync, isPending: loading } = useApiMutation(
    'delete',
    '/connections/{id}'
  );

  const submit = async (isSecondConfirm: boolean) => {
    if (!connectionId) {
      captureSentryError('Connection to delete missing');

      showWarnNotification({
        title: 'Missing connection',
        message:
          'Connection to delete missing. Please reach out to support if the issue persists.',
      });

      return;
    }

    return await mutateAsync({
      params: {
        path: {
          id: connectionId,
        },
        query: {
          onLocked: isSecondConfirm ? 'archive' : 'error',
        },
      },
    })
      .then((res) => {
        showSuccessNotification({
          title: 'Success!',
          message: `Connection was ${res.data.status} successfully.`,
        });

        if (res.data.status === 'deleted') {
          if (window.location.pathname.startsWith('/connection/')) {
            goto('/connections');
          }
          if (detailDrawerOpened) {
            closeConnectionDetailDrawer();
          }
        }

        closeModal();
        invalidate();
      })
      .catch((err: ApiDeleteError) => {
        if (err.context?.canArchive) {
          return {
            showConfirm: true,
          };
        } else {
          showApiErrorNotification({
            error: err,
            title: 'Failed to delete connection',
            defaultMessage:
              'We failed to delete connection. Please reach out to support if the issue persists.',
          });
        }
      });
  };

  return (
    <ConfirmModal
      type="delete"
      opened={opened}
      closeModal={closeModal}
      loading={loading}
      onSubmit={submit}
      title={'Delete this connection?'}
      subtitle={
        'Are you sure you want to delete this connection? This action cannot be undone.'
      }
      secondConfirm={{
        title: 'We have found data associated with this connection',
        subtitle:
          'We have found data associated with this connection. Would you like to archive the connection instead? This will keep the data associated with the connection, but will remove the connection from your dashboard.',
        submitTitle: 'Archive connection',
      }}
    />
  );
};
